<template>
  <div class="tooltip" :class="{ 'tooltip--bottom': placement === 'bottom' }">
    <slot />
    <div class="top">
      <div :class="texts.length ? 'txts-container' : 'txt-container'">
        <div class="bold-tooltip" v-if="boldText">{{ $t(boldText) }}</div>
        <template v-if="texts.length">
          <div v-for="(text, idx) in texts" :key="text + idx" :class="isCapitalized ? 'capitalize' : ''">
            {{ $t(text) }}
          </div>
        </template>
        <div v-else class="tooltip-text">{{ $t(text) }}</div>
      </div>
      <i></i>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    text: { required: false, type: [String, Number] },
    boldText: { required: false, type: String },
    texts: { required: false, type: Array, default: () => [] },
    isCapitalized: { required: false, type: Boolean, default: true },
    /** 'top' (default) or 'bottom' – position of the tooltip popup relative to the trigger */
    placement: { type: String, default: 'top', validator: v => v === 'top' || v === 'bottom' },
  },
}
</script>

<style lang="scss" scoped>
@use "@/styles/mixins" as *;

.tooltip {
  display: inline-block;
  position: relative;
  text-align: left;
}

.txts-container {
  display: flex;
  flex-direction: column;
  gap: 0;
  line-height: 1.15;
}

.txt-container {
  @include flex-center;
  .tooltip-text {
    white-space: pre-line;
  }
}

.bold-tooltip {
  font-weight: $font-weight-bold;
}

.tooltip .top {
  top: -10px;
  left: 50%;
  min-width: max-content;
  max-width: min(320px, calc(100vw - 24px));
  transform: translate(-50%, -100%);
  padding: 10px;
  color: $black-blue;
  background-color: $light-grey;
  font-weight: normal;
  font-size: $font-size-smaller;
  border-radius: 8px;
  position: absolute;
  box-sizing: border-box;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
  display: none;
  z-index: 10000;
  white-space: pre-line;
  word-break: break-word;
}

.tooltip:hover .top {
  display: block;
}

/* Default: tooltip above trigger, arrow below bubble */
.tooltip .top i {
  position: absolute;
  top: 100%;
  left: 50%;
  margin-left: -15px;
  width: 30px;
  height: 15px;
  overflow: hidden;
}

.tooltip .top i::after {
  content: '';
  position: absolute;
  width: 15px;
  height: 15px;
  left: 60%;
  transform: translate(-50%, -50%) rotate(45deg);
  background-color: $light-grey;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.5);
}

/* placement=bottom: tooltip below trigger, arrow above bubble */
.tooltip--bottom .top {
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  @include rtl {
    left: 50%;
    transform: translateX(-50%);
  }
}

.tooltip--bottom .top i {
  top: -15px;
  left: 50%;
  margin-left: -15px;
  width: 30px;
  height: 15px;
}

.tooltip--bottom .top i::after {
  bottom: 0;
  top: auto;
  left: 50%;
  transform: translate(-50%, 50%) rotate(45deg);
  box-shadow: 1px 1px 2px rgba(0, 0, 0, 0.2);
}

/* Mobile: show below trigger and align to the right edge (icons sit on the right in card rows) */
@media (max-width: 820px) {
  .tooltip:not(.tooltip--bottom) .top {
    top: calc(100% + 8px);
    bottom: auto;
    left: auto;
    right: 0;
    transform: none;
    min-width: 0;
    width: max-content;
    max-width: min(320px, calc(100vw - 32px));
  }

  .tooltip:not(.tooltip--bottom) .top i {
    top: -15px;
    left: auto;
    right: 12px;
    margin-left: 0;
  }

  .tooltip:not(.tooltip--bottom) .top i::after {
    left: 50%;
    top: auto;
    bottom: 0;
    transform: translate(-50%, 50%) rotate(45deg);
  }

  .tooltip--bottom .top {
    left: auto;
    right: 0;
    transform: none;
    max-width: min(320px, calc(100vw - 32px));
  }

  .tooltip--bottom .top i {
    left: auto;
    right: 12px;
    margin-left: 0;
  }
}

</style>
